import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import get from 'lodash/get';

import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Box from '@mui/material/Box';
import makeStyles from '@mui/styles/makeStyles';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuItem from '@mui/material/MenuItem';

import { theme } from 'theme';

import { downloadService, uploadPortfolio, confirmPortfolioESG } from 'services';
import { portfolioUploadSteps as steps } from 'shared/constants/portfolioUploadSteps';
import { tableRefreshReasonTypes as refreshReasons } from 'shared/constants/tableRefreshReasonTypes';
import { portfolioUploadSchema, creditReviewCreatePortfolioSchema } from 'shared/validations';
import { useNotifications } from 'shared/hooks';
import { Loader } from 'ui/Loader/Loader';
import { getDossiersAction, refreshDossiersAction } from 'store/portfolio/dossierActions';
import { formatNumber } from 'shared/functions';
import { getAuthDataOrganisationId } from 'selectors/auth';

const useStyles = makeStyles({
  downloadTemplateContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  buttonIcon: {
    marginRight: theme.spacing(1),
  },
  downloadButtonContainer: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'flex-end',
  },
  input: {
    display: 'none',
  },
  uploadContainer: {
    padding: '2px 4px',
    margin: theme.spacing(2, 0),
    display: 'flex',
    alignItems: 'center',
  },
  fileUploadText: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  fullWidth: {
    width: '100%',
  },
  summaryContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  textCenter: {
    textAlign: 'center',
  },
  checkIcon: {
    fontSize: 80,
  },
  score: {
    fontSize: 40,
    fontWeight: 'bold',
  },
  strong: {
    fontWeight: 700,
  },
  selectButton: {
    width: 150,
    height: 100,
    textTransform: 'inherit',
    border: 'none',
    color: theme.palette.text.primary,
    boxShadow: '0 4px 4px 2px rgba(155, 155, 155, 0.3)',
    padding: '0 35px',
  },
  selectTitle: {
    fontSize: 15,
  },
});

const PortfolioUploadESG = ({
  portfolioType,
  ratingType,
  onClose,
  backToTypeSelection,
  onClickCreateNew,
  createdPortfolioName,
  createdPortfolioDesc,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { notifyError } = useNotifications();
  const navigate = useNavigate();

  const organisationId = useSelector(getAuthDataOrganisationId);

  const {
    data: portfoliosData = [],
    isPending: isPortfoliosPending,
    shouldRefresh,
  } = useSelector(({ dossier }) => dossier.byRatingType[ratingType]);

  const [fileUploadInputRef, setFileUploadInputRef] = useState(null);

  const [isPending, setIsPending] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(steps.UPLOAD);
  const [summaryStep, setSummaryStep] = useState(0);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [tempDossierId, setTempDossierId] = useState();
  const [portfolios, setPortfolios] = useState([]);

  useEffect(() => {
    setPortfolios(
      portfoliosData &&
        portfoliosData
          .map(([, [id, name, , isDedicated], , , ,]) => ({
            id,
            name,
            isDedicated,
          }))
          .filter((dossier) => !dossier.isDedicated),
    );
  }, [portfoliosData]);

  const didUnmount = React.useRef(false);

  useEffect(() => {
    dispatch(getDossiersAction({ ratingType, requestParams: { limit: 100 } }));
    return () => {
      didUnmount.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefresh]);

  useEffect(() => {
    if (error) {
      notifyError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const downloadMissingIsinsErrorHandler = () => {
    notifyError(t('ESG_PORTFOLIO_UPLOAD.FAILED_DOWNLOAD_MISSING_ELEMENTS_TEMPLATE'));
  };

  const handleDownloadMissingIsinsClick = (missingIsins) => {
    downloadService({
      path: '/esg/api/portfolio-not-found-isins-template/',
      name: 'portfolio-not-found-isins-list.csv',
      options: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(missingIsins),
      },
      onError: downloadMissingIsinsErrorHandler,
    });
  };

  const available = get(data, 'available', 0);
  const missingIsinsCount = (data && data.notFoundIsins && data.notFoundIsins.length) || 0;
  const missingScoresCount = get(data, 'unavailable', 0) - missingIsinsCount;
  const total = data ? data.total : 0;
  const summarySteps = [
    {
      label: t('UPLOAD.STEP_ONE'),
      text: t('ESG.PORTFOLIO_UPLOAD.STEP_SCORES_AVAILABLE'),
      ratingsCount: available,
    },
    {
      label: t('UPLOAD.STEP_TWO'),
      text: t('ESG.PORTFOLIO_UPLOAD.STEP_SCORES_MISSING'),
      ratingsCount: missingScoresCount,
    },
    {
      label: t('UPLOAD.STEP_THREE'),
      text: missingIsinsCount
        ? t('ESG.PORTFOLIO_UPLOAD.STEP_COMPANIES_MISSING')
        : t('ESG.PORTFOLIO_UPLOAD.STEP_NO_COMPANIES_MISSING'),
      ratingsCount: missingIsinsCount,
      customField: missingIsinsCount ? (
        <Typography className={classes.textCenter}>
          <Button
            component="a"
            onClick={() => handleDownloadMissingIsinsClick(data.notFoundIsins)}
            download="portfolio-not-found-isins-list.csv"
            variant="contained"
            color="secondary"
          >
            {t('ESG.PORTFOLIO_UPLOAD.DOWNLOAD_UNIDENTIFIED_ELEMENTS')}
          </Button>
        </Typography>
      ) : undefined,
    },
  ];

  const sampleTemplateUrl = '/esg/api/upload-portfolio/';

  const fileUploadInputRefChanged = (element) => setFileUploadInputRef(element);

  const placeOrders = async () => {
    setIsPending(true);
    try {
      const confirmBody = {
        sourceDossierId: tempDossierId,
      };

      if (typeof selectedPortfolio === 'string') {
        confirmBody.targetDossierId = selectedPortfolio;
      } else if (typeof selectedPortfolio === 'object') {
        confirmBody.targetDossierName = createdPortfolioName;
        confirmBody.targetDossierDescription = createdPortfolioDesc;
      }

      await confirmPortfolioESG(confirmBody);
      dispatch(
        refreshDossiersAction({
          reason: refreshReasons.UPDATED,
          ratingType,
        }),
      );
    } catch (e) {
      setError(e.message);
    }
    setStep(steps.FINISH);
    setIsPending(false);
  };

  const handleStepSubmit = async (payload) => {
    if (step === steps.UPLOAD) {
      setIsPending(true);
      try {
        const uploadData = await uploadPortfolio({
          portfolio: payload && payload.fileUploadInput,
          type: portfolioType,
          organisationId,
        });
        setData({ ...uploadData, fileName: payload.fileUploadInput.name });
        setTempDossierId(uploadData.dossierId);
        setStep(steps.CREATE_DOSSIER);
      } catch (e) {
        setError(e.message);
      }
      setIsPending(false);
    } else if (step === steps.CREATE_DOSSIER) {
      setStep(steps.SUMMARY);
    }
  };

  useEffect(() => {
    if (createdPortfolioName && step === steps.CREATE_DOSSIER) {
      setSelectedPortfolio({ createdPortfolioName, createdPortfolioDesc });
      handleStepSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdPortfolioName, createdPortfolioDesc]);

  const onSelectDossier = (e, defaultAction) => {
    setSelectedPortfolio(e.target.value);
    if (defaultAction) {
      defaultAction(e);
    }
  };

  const downloadErrorHandler = () => {
    notifyError(t('ESG.PORTFOLIO_UPLOAD.FAILED_DOWNLOAD_PORTFOLIO_TEMPLATE'));
  };

  const closeModal = () => {
    setSummaryStep(0);
    onClose();
  };

  const handleDownloadTemplateClick = () =>
    downloadService({
      path: sampleTemplateUrl,
      name: 'esg-portfolio-template.csv',
      onError: downloadErrorHandler,
    });

  const handleDone = async () => {
    closeModal();
    navigate('/portfolios');
  };

  const previousStep = () => {
    switch (step) {
      case steps.UPLOAD:
        backToTypeSelection();
        break;
      case steps.SUMMARY:
        if (summaryStep > 0) {
          setSummaryStep(summaryStep - 1);
        } else {
          setStep(step - 1);
        }
        break;
      default:
        setStep(step - 1);
    }
  };

  const nextSummaryStep = () => {
    if (summaryStep < summarySteps.length - 1) {
      setSummaryStep(summaryStep + 1);
    } else {
      placeOrders();
    }
  };

  const updateUploadBoxData = (setFieldValueFunc, file, text) => {
    if (!file && fileUploadInputRef) {
      fileUploadInputRef.value = null;
    }
    setFieldValueFunc('fileUploadInput', file);
    setFieldValueFunc('fileUploadText', text);
  };

  const stepUploadInitialValues = {
    description: '',
    origin: window.location.href,
    fileUploadText: '',
    fileUploadInput: null,
  };

  const stepCreateDossierInitialValues = {
    dossierId: '',
  };

  const stepUpload = (
    <Formik
      key={steps.UPLOAD}
      initialValues={stepUploadInitialValues}
      onSubmit={(payload) => handleStepSubmit(payload)}
      validationSchema={portfolioUploadSchema(['csv', 'xls', 'xlsx'])}
    >
      {({ values, errors, touched, setFieldValue }) => {
        return (
          <Form>
            <DialogContent>
              <Box mb={2}>
                <Typography>{t('ESG.PORTFOLIO_UPLOAD.INFO_1')}</Typography>
              </Box>
              <Box mb={2}>
                <Typography>{t('ESG.PORTFOLIO_UPLOAD.INFO_2')}</Typography>
              </Box>
              <Box mb={2}>
                <Typography className={classes.strong}>
                  {t('ESG.PORTFOLIO_UPLOAD.INFO_3')}
                </Typography>
              </Box>
              <Divider />
              <div className={classes.downloadTemplateContainer}>
                <Box p={2}>
                  <Typography>{`${t('UPLOAD.FILE_FORMAT')}: .csv, .xls, .xlsx`}</Typography>
                  <Typography>{`${t('UPLOAD.MAX_SIZE')}: 100 ${t(
                    'ENTITIES',
                  ).toLowerCase()}`}</Typography>
                  <Typography>{`${t('UPLOAD.COLUMNS')}: ISIN, LEI`}</Typography>
                </Box>
                <Box p={2} className={classes.downloadButtonContainer}>
                  <Button
                    onClick={handleDownloadTemplateClick}
                    variant="outlined"
                    disabled={isPending || isPortfoliosPending}
                  >
                    {t('DOWNLOAD_TEMPLATE')}
                  </Button>
                </Box>
              </div>
              <Divider />
              <ErrorMessage name="fileUploadInput">
                {(message) => (
                  <Box mt={2}>
                    <Typography color="error">{message}</Typography>
                  </Box>
                )}
              </ErrorMessage>
              <Paper className={classes.uploadContainer}>
                <input
                  className={classes.input}
                  id="file-upload"
                  name="file-upload"
                  ref={fileUploadInputRefChanged}
                  type="file"
                  onChange={(event) => {
                    updateUploadBoxData(
                      setFieldValue,
                      event.currentTarget.files[0],
                      get(event.currentTarget.files, '[0].name', ''),
                    );
                  }}
                />
                <Field name="fileUploadText">
                  {({ field }) => (
                    <InputBase
                      {...field}
                      className={classes.fileUploadText}
                      placeholder={t('UPLOAD.NO_FILE_SELECTED')}
                      error={errors.fileUploadInput && touched.fileUploadText}
                      readOnly
                    />
                  )}
                </Field>
                <IconButton
                  type="submit"
                  disabled={!fileUploadInputRef || !fileUploadInputRef.value}
                  onClick={() => {
                    updateUploadBoxData(setFieldValue, null, '');
                  }}
                  size="large"
                >
                  <DeleteIcon />
                </IconButton>
                <label htmlFor="file-upload">
                  <Button variant="contained" color="primary" component="span">
                    {t('UPLOAD.BROWSE')}
                  </Button>
                </label>
              </Paper>
            </DialogContent>
            <DialogActions>
              {step !== steps.FINISH && (
                <Box>
                  <Button variant="outlined" onClick={previousStep}>
                    {t('PREVIOUS')}
                  </Button>
                </Box>
              )}
              <div style={{ flex: '1 0 0' }} />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isPending || isPortfoliosPending || !get(values, 'fileUploadInput')}
              >
                {t('NEXT')}
              </Button>
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );

  const stepCreateDossier = (
    <Formik
      key={steps.CREATE_DOSSIER}
      className={classes.fullWidth}
      initialValues={stepCreateDossierInitialValues}
      onSubmit={(payload) => handleStepSubmit(payload)}
      validationSchema={creditReviewCreatePortfolioSchema}
    >
      {() => {
        return (
          <Form>
            <DialogContent>
              <Box mb={2}>
                <Typography>{t('UPLOAD.UPLOAD_SELECTED')}</Typography>
              </Box>
              <div className={classes.downloadTemplateContainer}>
                <Box p={2}>
                  <Typography color="primary">{`${data.fileName}`}</Typography>
                </Box>
                <Box p={2} className={classes.downloadButtonContainer}>
                  <Button
                    onClick={() => setStep(steps.UPLOAD)}
                    variant="contained"
                    color="primary"
                    disabled={isPending || isPortfoliosPending}
                  >
                    {t('UPLOAD.CHANGE_FILE')}
                  </Button>
                </Box>
              </div>
              <Box mt={2}>
                <Typography>{t('ESG.PORTFOLIO_UPLOAD.CREATE_PORTFOLIO')}</Typography>
              </Box>
              <Box display="flex" alignItems="center">
                {!!portfolios.length && (
                  <Field name="dossierId">
                    {({ field }) => (
                      <TextField
                        {...field}
                        select
                        label={t('ESG.PORTFOLIO_UPLOAD.SELECT_PORTFOLIO')}
                        fullWidth
                        onChange={(e) => onSelectDossier(e, field.onChange)}
                      >
                        {portfolios.map(({ id, name }) => (
                          <MenuItem key={id} value={id}>
                            {name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Field>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Box>
                <Button variant="outlined" onClick={previousStep}>
                  {t('PREVIOUS')}
                </Button>
              </Box>
              <div style={{ flex: '1 0 0' }} />
              <Button variant="outlined" onClick={onClickCreateNew}>
                {t('Create new portfolio')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isPending || isPortfoliosPending}
              >
                {t('NEXT')}
              </Button>
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );

  const stepSummary = (
    <>
      <DialogContent>
        {summarySteps.length > 1 ? (
          <Box mb={2}>
            <Stepper activeStep={summaryStep}>
              {summarySteps.map((item) => {
                return (
                  <Step key={item.label}>
                    <StepLabel>{item.label}</StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Box>
        ) : null}
        <Box mb={2} className={classes.summaryContent}>
          <Typography color="primary" variant="h3">
            {summarySteps[summaryStep].ratingsCount ? summarySteps[summaryStep].ratingsCount : 0}
          </Typography>
          <Box p={2}>
            <Typography variant="h6">{t('UPLOAD.OUT_OF')}</Typography>
          </Box>
          <Typography variant="h3">{formatNumber(total)}</Typography>
        </Box>
        <Box p={2} mb={2}>
          <Typography variant="body1" className={classes.textCenter}>
            {summarySteps[summaryStep].text}
          </Typography>
        </Box>
        {summarySteps[summaryStep].customField ? (
          <Box p={2}>{summarySteps[summaryStep].customField}</Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        {step !== steps.FINISH && (
          <Box>
            <Button variant="outlined" onClick={previousStep}>
              {t('PREVIOUS')}
            </Button>
          </Box>
        )}
        <div style={{ flex: '1 0 0' }} />
        <Button
          variant="contained"
          color="primary"
          disabled={isPending || isPortfoliosPending}
          onClick={nextSummaryStep}
        >
          {t('NEXT')}
        </Button>
      </DialogActions>
    </>
  );

  const stepFinish = (
    <>
      <DialogContent className={classes.textCenter}>
        <Box
          width={140}
          height={140}
          borderRadius="50%"
          display="flex"
          justifyContent="center"
          alignItems="center"
          marginY={2}
          marginX="auto"
          boxShadow="0 3px 4px 1px rgba(155, 155, 155, 0.3)"
          bgcolor={theme.palette.custom.verySoftCyan}
        >
          <p className={classes.score}>{data ? data.portfolioScore : 0}</p>
        </Box>
      </DialogContent>
      <DialogActions>
        {/* <Button variant="outlined" onClick={closeModal}>
          {t('CANCEL')}
        </Button> */}
        <Button
          variant="contained"
          color="primary"
          disabled={isPending || isPortfoliosPending}
          onClick={handleDone}
        >
          {t('ESG.PORTFOLIO_UPLOAD.GOTO_PORTFOLIOS')}
        </Button>
      </DialogActions>
    </>
  );

  const stepsMapping = {
    [steps.UPLOAD]: stepUpload,
    [steps.CREATE_DOSSIER]: stepCreateDossier,
    [steps.SUMMARY]: stepSummary,
    [steps.FINISH]: stepFinish,
  };

  return (
    <>
      {stepsMapping[step]}
      {(isPending || isPortfoliosPending) && <Loader cover />}
    </>
  );
};

PortfolioUploadESG.defaultProps = {
  onClose: () => null,
  backToTypeSelection: () => null,
  onClickCreateNew: () => null,
  createdPortfolioName: undefined,
  createdPortfolioDesc: undefined,
};

PortfolioUploadESG.propTypes = {
  portfolioType: PropTypes.string.isRequired, // portfolioTypes
  ratingType: PropTypes.string.isRequired, // ratingTypes
  onClose: PropTypes.func,
  onClickCreateNew: PropTypes.func,
  createdPortfolioName: PropTypes.string,
  createdPortfolioDesc: PropTypes.string,
  backToTypeSelection: PropTypes.func,
};

export default PortfolioUploadESG;
